import React from 'react'
import { Link } from 'react-router-dom'
import PropTypes from 'prop-types'

import bgPattern from '../../assets/images/bg-pattern.png'

/**
 * Landing Component
 *
 * A React component that represents the landing page of a website. It includes a navigation bar, a hero section indicating maintenance status, and a login modal. The component conditionally renders different elements based on the user's login status.
 *
 * @module website/Landing
 * @description A React component that represents the landing page of a website.
 * It includes a navigation bar, a hero section indicating maintenance status, and a login modal.
 * The component conditionally renders different elements based on the user's login status.
 * @example
 * const envData = {
 *   REACT_APP_THEME_LOGO: '/path/to/logo.png'
 * };
 * return (
 *   <Landing
 *     envData={envData}
 *     isLocalEnvironment="true"
 *     isLoggedIn={false}
 *   />
 * );
 *
 * @param {Object} envData - Environment-specific data, including logo paths.
 *
 * @returns {React.Element} The rendered landing page component.
 */
export const Landing = ({ envData }) => {
  return (
    <div className="trtui-layout-wrapper trtui-landing">
      <nav
        className="trtui-navbar trtui-navbar-expand-lg trtui-navbar-landing trtui-fixed-top"
        id="navbar"
      >
        <div className="trtui-container">
          <Link className="trtui-navbar-brand" to="/">
            <img
              src={envData.REACT_APP_THEME_LOGO}
              className="trtui-card-logo trtui-card-logo-dark"
              alt="logo dark"
            />
            <img
              src={envData.REACT_APP_THEME_LOGO}
              className="trtui-card-logo trtui-card-logo-light"
              alt="logo light"
            />
          </Link>

          <div className="trtui-collapse trtui-navbar-collapse" id="navbarSupportedContent">
            <ul
              className="trtui-navbar-nav trtui-mx-auto trtui-mt-2 trtui-mt-lg-0"
              id="navbar-example"
            ></ul>
            <a href="/console/logout" className="trtui-btn trtui-btn-primary">
              Login <i className="ri-arrow-right-line trtui-align-middle trtui-ms-1" />
            </a>
          </div>
        </div>
      </nav>
      <section
        className="trtui-section trtui-pb-0 trtui-hero-section"
        style={{ height: '100vh' }}
        id="hero"
      >
        <div
          className="trtui-bg-overlay trtui-bg-overlay-pattern"
          style={{ backgroundImage: `url(${bgPattern})` }}
        />
        <div className="trtui-d-sm-block trtui-d-lg-none" style={{ paddingTop: '8rem' }}>
          &nbsp;
        </div>
        <div className="trtui-container" style={{ paddingBottom: '3rem' }}>
          <div className="trtui-row trtui-justify-content-center">
            <div className="trtui-col-lg-8 trtui-col-sm-10">
              <div className="trtui-text-center trtui-mt-lg-5 trtui-pt-5">
                <h1 className="trtui-display-6 trtui-fw-semibold trtui-mb-3 trtui-lh-base">
                  Website Under <span className="trtui-text-secondary">Maintenance </span>
                </h1>
                <p className="trtui-lead trtui-text-muted trtui-lh-base">
                  This website is currently undergoing scheduled maintenance. We should be back
                  shortly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <div style={{ position: 'fixed', bottom: '1rem', right: '1rem' }}>
        <Link to="/" className="trtui-text-decoration-none">
          Contact Web Admin
        </Link>
      </div>
    </div>
  )
}

Landing.propTypes = {
  envData: PropTypes.object.isRequired,
  isLocalEnvironment: PropTypes.string.isRequired,
}
